import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUsersWithSubscriptions } from "@/hooks/users/useUsersWithSubscriptions";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface CloneDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentWithMetadata | null;
  onConfirm: (name: string, userId: string) => void;
  isLoading?: boolean;
}

export function CloneDocumentModal({
  isOpen,
  onClose,
  document,
  onConfirm,
  isLoading,
}: CloneDocumentModalProps) {
  const [cloneName, setCloneName] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const { data: usersData } = useUsersWithSubscriptions({}, 1, 100);
  const users = (usersData?.profiles ?? []).sort((a, b) =>
    (a.email || "").localeCompare(b.email || ""),
  );

  useEffect(() => {
    if (document) {
      setCloneName(document.name);
    }
  }, [document, isOpen]);

  const handleConfirm = () => {
    if (!cloneName.trim() || !selectedUserId) return;
    onConfirm(cloneName, selectedUserId);
    handleClose();
  };

  const handleClose = () => {
    setCloneName("");
    setSelectedUserId("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Clone Document</DialogTitle>
          <DialogDescription>
            Create a copy of "{document?.name}" with a new name and assign it to a user.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Document Name</label>
            <Input
              placeholder="Enter document name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              disabled={isLoading}
              autoFocus
              className="w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Assign to User</label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!cloneName.trim() || !selectedUserId || isLoading}
          >
            {isLoading ? "Cloning..." : "Clone"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
